import { useCallback, useRef, useState } from "react";
import { getConfig } from "../lib/configMgr";
import { useStreamConnector } from "../hooks/useStreamConnector";
import "./NetworkTestPage.css";

const { ExternalRESTAPI } = getConfig();

// Matches the real server heartbeat cadence (see network_test.py / useStreamConnector.js) —
// used to size how many agent-status polls to run so the test takes a comparable amount
// of time to the SSE/poll test above it.
const AGENT_STATUS_POLL_COUNT = 4;
const AGENT_STATUS_POLL_INTERVAL_MS = 10 * 1000;

const STATUS = { PENDING: "pending", RUNNING: "running", PASS: "pass", WARN: "warn", FAIL: "fail" };

function StatusRow({ label, status, detail }) {
  return (
    <div className={`network-test-row network-test-row--${status}`}>
      <span className="network-test-row__icon" aria-hidden="true">
        {status === STATUS.PASS ? "✓" : status === STATUS.WARN ? "⚠" : status === STATUS.FAIL ? "✕" : "…"}
      </span>
      <span className="network-test-row__label">{label}</span>
      {detail && <span className="network-test-row__detail">{detail}</span>}
    </div>
  );
}

async function timedFetch(url, options) {
  const start = performance.now();
  try {
    const res = await fetch(url, options);
    return { ok: res.ok, status: res.status, ms: Math.round(performance.now() - start) };
  } catch (err) {
    return { ok: false, error: err?.message, ms: Math.round(performance.now() - start) };
  }
}

function navigationTiming() {
  const [nav] = performance.getEntriesByType("navigation");
  if (!nav) return null;
  return {
    dns_ms: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
    connect_ms: Math.round(nav.connectEnd - nav.connectStart),
    tls_ms: nav.secureConnectionStart > 0 ? Math.round(nav.connectEnd - nav.secureConnectionStart) : null,
    ttfb_ms: Math.round(nav.responseStart - nav.requestStart),
  };
}

const NetworkTestPage = () => {
  const [running, setRunning] = useState(false);
  const [health, setHealth] = useState({ status: STATUS.PENDING });
  const [timing, setTiming] = useState(null);
  const [agentStatus, setAgentStatus] = useState({ status: STATUS.PENDING, detail: "" });
  const [sseResult, setSseResult] = useState({ status: STATUS.PENDING, detail: "" });
  const [streamId, setStreamId] = useState(null);
  const reportRef = useRef({});

  const onEvent = useCallback(() => {}, []);
  const onError = useCallback(() => {
    setSseResult({ status: STATUS.FAIL, detail: "connection failed" });
  }, []);
  const onPollingModeChange = useCallback((polling) => {
    setSseResult({
      status: polling ? STATUS.WARN : STATUS.PASS,
      detail: polling
        ? "real-time streaming blocked — fell back to polling (this is the Novartis-proxy pattern)"
        : "real-time streaming connected",
    });
    reportRef.current.sse_polling_fallback = polling;
  }, []);

  useStreamConnector({
    streamId,
    auth: null,
    onEvent,
    onError,
    onPollingModeChange,
    endpoints: {
      stream: ExternalRESTAPI.NetworkTestStream,
      probe: ExternalRESTAPI.NetworkTestProbe,
    },
  });

  const runAgentStatusPoll = useCallback(async () => {
    // PLEX-6259: the reconnect-time status poll is by stream now (/agent is retired).
    const url = (ExternalRESTAPI.NetworkTestStreamStatus || "").replace("{streamId}", "network-test");
    const results = [];
    for (let i = 0; i < AGENT_STATUS_POLL_COUNT; i++) {
      results.push(await timedFetch(url));
      if (i < AGENT_STATUS_POLL_COUNT - 1) {
        await new Promise((resolve) => setTimeout(resolve, AGENT_STATUS_POLL_INTERVAL_MS));
      }
    }
    reportRef.current.agent_status_polls = results;
    const failures = results.filter((r) => !r.ok).length;
    setAgentStatus({
      status: failures === 0 ? STATUS.PASS : failures === results.length ? STATUS.FAIL : STATUS.WARN,
      detail: `${results.length - failures}/${results.length} polls succeeded`,
    });
  }, []);

  const runTest = useCallback(async () => {
    setRunning(true);
    reportRef.current = { started_at: new Date().toISOString(), user_agent: navigator.userAgent };

    const nav = navigationTiming();
    setTiming(nav);
    reportRef.current.navigation_timing = nav;

    const healthResult = await timedFetch(ExternalRESTAPI.Health);
    reportRef.current.health = healthResult;
    setHealth({
      status: healthResult.ok ? STATUS.PASS : STATUS.FAIL,
      detail: healthResult.ok ? `${healthResult.ms}ms` : healthResult.error || `HTTP ${healthResult.status}`,
    });

    try {
      const startedAt = performance.now();
      const res = await fetch(ExternalRESTAPI.NetworkTestStart);
      const body = res.ok ? await res.json() : null;
      reportRef.current.network_test_start = { ok: res.ok, status: res.status, ms: Math.round(performance.now() - startedAt) };
      if (body?.channel) {
        // Triggers the useStreamConnector effect above, which starts the real SSE/poll transport test.
        setStreamId(body.channel);
      } else {
        setSseResult({ status: STATUS.FAIL, detail: "could not start test channel" });
      }
    } catch (err) {
      setSseResult({ status: STATUS.FAIL, detail: err?.message || "could not start test channel" });
    }

    await runAgentStatusPoll();
    setRunning(false);
  }, [runAgentStatusPoll]);

  const copyReport = useCallback(() => {
    const report = JSON.stringify(reportRef.current, null, 2);
    navigator.clipboard?.writeText(report);
  }, []);

  return (
    <div className="network-test-page">
      <h1>Network Diagnostic</h1>
      <p>
        This checks whether your current network lets Plex Search work properly — including
        real-time updates, which some corporate proxies block or interfere with. No login
        needed. Nothing here is sent anywhere unless you click &quot;Copy report&quot; and
        paste it yourself.
      </p>

      <button onClick={runTest} disabled={running}>
        {running ? "Running…" : "Run test"}
      </button>

      <div className="network-test-results">
        <StatusRow label="Basic connectivity" status={health.status} detail={health.detail} />
        <StatusRow label="Real-time updates (SSE/polling)" status={sseResult.status} detail={sseResult.detail} />
        <StatusRow label="Background status checks (agent polling)" status={agentStatus.status} detail={agentStatus.detail} />
      </div>

      {timing && (
        <p className="network-test-timing">
          Page load — DNS: {timing.dns_ms}ms, connect: {timing.connect_ms}ms
          {timing.tls_ms != null && `, TLS: ${timing.tls_ms}ms`}, time to first byte: {timing.ttfb_ms}ms
        </p>
      )}

      <button onClick={copyReport} disabled={running}>Copy report</button>

      <div className="network-test-downloads">
        <p>
          For a deeper check (DNS, proxy config, certificate inspection) that this page can&apos;t
          see from the browser, download and run one of these locally — results post automatically,
          nothing to copy/paste:
        </p>
        <a href="/network-test/novartis-proxy-diag.sh" download>Download for Mac/Linux</a>
        {" · "}
        <a href="/network-test/novartis-proxy-diag.ps1" download>Download for Windows</a>
      </div>
    </div>
  );
};

export default NetworkTestPage;
